import React from 'react'

import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, SvgIcon, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Tooltip } from '@scoir/tooltips'

import { getSegmentValue } from 'components/SegmentSelector/segment-service'

import Range from '../AcademicProfile/Range'
import { withContext } from '../Context'

export const SegmentName = 'NeighborhoodIncome'
const Operation = 'between'
const Min = 0
const Max = 10

const incomeDecileMap: Record<number, string> = {
  0: '$0',
  1: '$34.7k',
  2: '$40.8k',
  3: '$45.4k',
  4: '$49.8k',
  5: '$54k',
  6: '$58.8k',
  7: '$64.5k',
  8: '$73.2k',
  9: '$89.4k',
  10: '$250k +',
}

export const getLabelByIncomeDecile = (decile: number) => {
  return incomeDecileMap[decile]
}

const useStyles = makeStyles(() => ({
  infoIcon: {
    fontSize: '18px',
    alignSelf: 'center',
  },
}))

type NeighborhoodIncomeSelectorProps = {
  criteria: string
  onChange: (criteria: string, segmentName: string, value: any, operation: string) => void
  segment: object
  segmentName: string
  label: string
}

export const NeighborhoodIncomeSelector = ({
  criteria = 'Must',
  onChange,
  segment,
  label,
}: NeighborhoodIncomeSelectorProps) => {
  const currentValue = getSegmentValue(segment, criteria, SegmentName)
  const [from, setFrom] = React.useState(currentValue[0] - 1 || Min) // subtract 1 to the from value to display properly
  const [to, setTo] = React.useState(currentValue[1] || Max)
  const classes = useStyles()

  return (
    <>
      <Box
        pt={2}
        display="flex"
        justifyContent="space-between"
      >
        <Box>
          <Typography
            variant="subtitle2"
            color="textPrimary"
            className="title"
          >
            Neighborhood Household Income
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
          >
            Min and max median income
          </Typography>
        </Box>
        <Tooltip
          title="Based on US Census Income Data"
          placement="top"
        >
          <SvgIcon
            color="primary"
            className={classes.infoIcon}
          >
            <FontAwesomeIcon
              icon={faInfoCircle}
              size="xs"
            />
          </SvgIcon>
        </Tooltip>
      </Box>
      <Box
        px={1}
        pt={2}
        pb={1}
      >
        <Range
          fixedLabelsWithMarks
          hideValueLabel
          step={1}
          min={Min}
          max={Max}
          from={from}
          to={to}
          getAriaValueText={(value: number) => `${label} range: ${value}`}
          formatLabel={(value: number) => getLabelByIncomeDecile(value)}
          onChange={(value: number[]): void => {
            const isDefaultValues = value[0] === Min && value[1] === Max

            // Add 1 to the from value to specify the decile
            const values = isDefaultValues ? [] : [value[0] + 1, value[1]]
            onChange(criteria, SegmentName, values, Operation)

            setFrom(value[0])
            setTo(value[1])
          }}
        />
      </Box>
    </>
  )
}

export default withContext(NeighborhoodIncomeSelector)
